import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useHistory } from 'react-router-dom'
import { useModal } from '../providers/modal-provider.js';
import { useAuthData } from '../includes/auth-provider.js';
import swal from 'sweetalert';
import QRCode from 'qrcode';
import { getCookie } from '../common_component/cookie.js';
import PageLoader from "../common_component/PageLoader.js";

export default function ProductQrGeneratorModal() {

    const { displayModal, modalData, closeModal, setModalData, setModalView } = useModal()
    const [loading, setLoading] = useState(false);
    const [qrFor, setQrFor] = useState([]);
    const [pageSize, setPageSize] = useState("Roller");
    const [stockInformation, setStockInformation] = useState([]);
    const [quantityAdjust, setQuantityAdjust] = useState(0);

    const { user, write, organization_id } = useAuthData(modalData.page_name);
    const history = useHistory();

    async function getDefaultQrGeneartionSettings() {
        setLoading(true);
        const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/get-default-bulk-qr-generation-setting`, { method: 'POST', body: JSON.stringify({ "organization_id": organization_id, "email": user.email, "page_name": modalData.page_name }), headers: { "Content-type": "application/json; charset=UTF-8", "token": getCookie("access_token") } }
        );
        const body = await response.json();
        if (body.data.defaultSetting !== null) {
            const temp = body.data.defaultSetting.settings.find(x => x.type == "Product").qrFor;
            setQrFor(temp);
            setPageSize(body.data.pageSize);
            setLoading(false);
        }
        else {
            swal({
                title: "Oh!",
                text: "You need to set QR settings first!",
                icon: "warning",
                buttons: {
                    cancel: "Close",
                    confirm: {
                        text: "Go to Settings",
                        value: true,
                    },
                },
            }).then((result) => {
                if (result) {
                    handleProductQrModalClose();
                    history.push("/bulk-qr-generation-settings");
                } else {
                    handleProductQrModalClose();
                }
            });
        }
    }

    const handleProductQrModalClose = () => {
        closeModal()
        setModalView(null)
        setModalData(null)
    }

    useEffect(() => {
        setQuantityAdjust(modalData.available_stock);
        setStockInformation(modalData.serial_batch_details);
        getDefaultQrGeneartionSettings();
    }, [])

    const parseQrText = (text) => {
        // Find the end of the PID (assuming it ends right before the product name)
        const pidMatch = text.match(/Pid\s+(\S+)/);
        const productNameMatch = text.match(/(Mutton Stew Delhi|Physec Mutton Stew Delhi|Product Name) - \d+/i); // Adjusted for a common product name format

        const pid = pidMatch ? pidMatch[1] : 'PID Not Found';
        let productName = 'Product Name Not Found';
        let remainingDetails = text;

        if (productNameMatch) {
            productName = productNameMatch[0].trim();
            // Remove PID and Product Name to get remaining details
            remainingDetails = text.replace(pidMatch ? pidMatch[0] : '', '').replace(productName, '').replace('|', '').trim();
        }

        return {
            pid: pid,
            productName: productName,
            details: remainingDetails.replace(/\|/g, '<br>').replace(/\s{2,}/g, ' ') // Replace remaining '|' with line breaks
        };
    };

    const directPrintQRWithImage = async (qrData = []) => {
        // Check if QRCode library is available
        if (typeof QRCode === 'undefined') {
            return alert("QRCode library is not loaded.");
        }

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return alert("Popup blocked. Please allow popups.");

        // Helper to extract value after the first colon, or return the whole string if no colon is present
        const extractValueAfterColon = (text) => {
            const colonIndex = text.indexOf(':');
            return colonIndex !== -1 ? text.substring(colonIndex + 1).trim() : text.trim();
        };

        // Helper to format date strings (YYYY-MM-DD to DD Mon YY)
        const formatDate = (dateString) => {
            if (!dateString) return '';
            try {
                // Attempt to parse date string (e.g., 2025-10-13)
                const dateParts = dateString.split('-');
                if (dateParts.length === 3) {
                    const year = dateParts[0].substring(2); // Last two digits of year
                    const monthIndex = parseInt(dateParts[1], 10) - 1; // Month index 0-11
                    const day = dateParts[2];

                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const month = monthNames[monthIndex];

                    return `${day} ${month} ${year}`;
                }
            } catch (e) {
                return dateString;
            }
            return dateString;
        };


        const parseQRText = (text) => {
            const parts = text.split(' | ').map(p => p.trim());
            const dataMap = {};

            // Convert the pipe-separated string into a simple key-value map
            parts.forEach(part => {
                const colonIndex = part.indexOf(':');
                if (colonIndex !== -1) {
                    const key = part.substring(0, colonIndex).trim();
                    const value = part.substring(colonIndex + 1).trim();
                    dataMap[key] = value;
                }
            });

            // 1. Extract and format Product Name
            const rawProductName = dataMap['Pname'] || 'Product Name N/A';
            const productName = extractValueAfterColon(rawProductName);

            // 2. Format Ptype and Batch together
            const ptypeValue = dataMap['Ptype'] || '';
            const batchValue = dataMap['Batch'] || '';
            const ptypeBatchLine = ptypeValue && batchValue
                ? `${ptypeValue} (${batchValue})`
                : ptypeValue || batchValue;

            // 3. Create a single combined header line for product name and type/batch
            const headerLine = productName;
            const subHeaderLine = ptypeBatchLine;


            // 4. Format other details (Batch, Mfg Date, Exp Date)
            const detailLines = [];

            // Add Manufacture Batch value
            if (dataMap['Manufacture_Batch']) {
                detailLines.push(dataMap['Manufacture_Batch']); // Showing only the value
            }

            // Add Manufacture Date
            if (dataMap['Manufacture_Date']) {
                const formattedDate = formatDate(dataMap['Manufacture_Date']);
                detailLines.push(`Mfg Date: ${formattedDate}`);
            }

            // Add Expiry Date
            if (dataMap['Expiry_Date']) {
                const formattedDate = formatDate(dataMap['Expiry_Date']);
                detailLines.push(`Exp Date: ${formattedDate}`);
            }

            // Return Header Line and the array of detail lines
            return { headerLine, subHeaderLine, details: detailLines };
        };

        const base64Images = await Promise.all(
            // Generate QR codes
            qrData.map(qr => QRCode.toDataURL(qr.text, { errorCorrectionLevel: 'H', margin: 1 }))
        );

        const html = `
    <html>
      <head>
        <title>QR Print Label - 1x4 Inch Roll</title>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            font-family: Arial, sans-serif;
          }

          /* Container for two labels per row. Total width ~4 inches (101.6mm) */
          .print-container {
            display: grid;
            grid-template-columns: repeat(2, 50.8mm); 
            gap: 0;
            justify-content: center;
            align-content: start;
            padding: 0; 
            width: 101.6mm; /* Exactly 4 inches */
          }

          /* Single label box: 2 inches wide x 1 inch high with a proper border */
          .qr-box {
            width: 50.8mm;   /* 2 inch */
            height: 25.4mm;  /* 1 inch */
            border: 1px solid #000; 
            display: flex;
            flex-direction: row; 
            align-items: stretch;
            padding: 1mm;
            box-sizing: border-box;
          }

          /* Left column for QR Code */
          .qr-code-area {
            display: flex;
            align-items: center; /* This centers the QR code vertically */
            justify-content: center;
            width: 16mm;
            height: 100%;
            flex-shrink: 0;
            margin-right: 2mm;
            }

            .qr-code-area img {
            width: 100%;
            height: auto;
            border: none;
            }

            /* Right column for Text Details */
            .text-details-area {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            text-align: left;
            overflow: hidden;
            line-height: 1;
            max-height: 100%;
            /* NEW: Add a top margin to visually align with the centered QR image's top edge */
            /* Calculation: (qr-code-area height - qr-image height) / 2 = (23.4mm - 16mm) / 2 = 3.7mm */
            margin-top: 3.7mm;
            }
          
          /* Common style for all text lines */
          .label-line {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            padding: 0;
          }

          /* Primary line: Product Name */
          .header-line {
            font-size: 7.5pt; 
            font-weight: bold;
            margin-bottom: 0.5mm; /* Space after the product name */
          }
          
          /* Secondary line: Ptype (Batch) */
          .sub-header-line {
            font-size: 6.5pt; 
            font-weight: normal;
            margin-bottom: 0.5mm; /* Space before the remaining details */
          }

          /* Detail lines: Mfg Batch, Mfg Date, Exp Date */
          .product-detail-line {
            font-size: 6.5pt; 
            margin-bottom: 0.1mm; /* Very tight spacing for details */
          }

          /* Print-specific styles */
          @media print {
            @page {
              size: 4in 1in; 
              margin: 0; 
            }
            body {
              margin: 0;
            }
            .qr-box {
              border: 1px solid #000 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${qrData.map((qr, i) => {
            const { headerLine, subHeaderLine, details } = parseQRText(qr.text);
            return `
              <div class="qr-box">
                <div class="qr-code-area">
                  <img src="${base64Images[i]}" alt="QR Code" />
                </div>
                <div class="text-details-area">
                  <div class="label-line header-line">${headerLine}</div>
                  ${subHeaderLine ? `<div class="label-line sub-header-line">${subHeaderLine}</div>` : ''}
                  
                  ${details.map(detail => `
                      <div class="label-line product-detail-line">${detail}</div>
                  `).join('')}
                </div>
              </div>
            `;
        }).join('')}
        </div>
        <script>
          window.onload = () => {
            setTimeout(() => window.print(), 300);
          };
        </script>
      </body>
    </html>`;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const directPrintQRWithImage2 = async (qrData = []) => {
        // 1. Open the print window
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return alert("Popup blocked. Please allow popups.");

        // Helper function to extract structured data from the full text
        // NOTE: This is a placeholder. You should adapt this to how your 'qr.text' is actually structured.
        const parseQRText = (text) => {
            // Example: "PID-B01 | Mutton Stew - 6 | 1 Kg | Batch: B1102 | Exp: 2025-10-07 | Mfg: 2025-10-02"
            const parts = text.split(' | ');
            const pid = parts[0] || 'N/A';
            const productName = parts[1] || 'Product Name N/A';
            // Extract the rest of the details for display below the main headers
            const details = parts.slice(2).join(' | ');
            return { pid, productName, details };
        };

        // 2. Generate all QR code Data URLs
        const base64Images = await Promise.all(
            qrData.map(qr => QRCode.toDataURL(qr.text, { errorCorrectionLevel: 'H', margin: 1 }))
        );

        // 3. Define the HTML and CSS for printing
        const html = `
    <html>
    <head>
        <title>QR Print Label</title>
        <style>
            /* Reset for minimal margins and box sizing for labels */
            html, body { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
                width: 100%;
                height: 100%;
            }

            body { 
                font-family: Arial, sans-serif; 
                color: #333;
            }

            /* Container for ALL QR boxes. Each box will be a 'page' / label. */
            .print-container {
                display: block;
            }
            
            /* **KEY STYLES for Single-Item-Per-Page/Label** */
            .qr-box {
                /* ❌ REMOVED: page-break-after: always; (Fixed top cutting) */
                /* ❌ REMOVED: margin-top: 2mm; */

                /* These dimensions are for visualization in the browser preview. */
                width: 100mm; 
                height: 70mm;
                /* ✅ CRITICAL: Safe internal padding to prevent content cutting */
                padding: 5mm; 
                box-sizing: border-box;
                overflow: hidden;
            }

            /* Flex layout to place QR (60%) and Text (40%) side-by-side */
            .content-wrapper {
                display: flex;
                align-items: flex-start; /* Align content to the top */
                height: 100%; 
            }

            /* QR Code Section - 60% Width */
            .qr-section {
                width: 60%; 
                flex-shrink: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                padding-right: 5px; 
            }

            .qr-section img {
                /* Reduced size for safety */
                width: 50mm; 
                height: 50mm;
                max-width: 100%;
                max-height: 100%;
                border: 1px solid #000;
                padding: 1px;
            }

            /* Text Label Section - 40% Width */
            .label-content {
                width: 40%; 
                flex-grow: 1;
                text-align: left;
                padding-top: 5px; 
                word-break: break-word; 
            }

            .detail-line {
                font-size: 8pt; 
                line-height: 1.3;
                margin-bottom: 3px;
            }
            
            .pid-line {
                font-size: 10pt;
                font-weight: bold;
                margin-bottom: 2px;
                color: #000;
            }

            .product-name-line {
                font-size: 10pt;
                font-weight: 600;
                margin-bottom: 5px;
                color: #555;
                border-bottom: 1px dashed #ccc;
                padding-bottom: 2px;
            }

            /* Print Media Adjustments */
            @media print {
                html, body {
                    margin: 0;
                    padding: 0;
                }
                /* ❌ REMOVED the .qr-box override here to keep the 5mm padding! */
            }
        </style>
    </head>
    <body>
        <div class="print-container">
            ${qrData.map((qr, i) => {
            const { pid, productName, details } = parseQRText(qr.text);
            return `
                <div class="qr-box">
                    <div class="content-wrapper">
                        <div class="qr-section">
                            <img src="${base64Images[i]}" alt="QR Code" />
                        </div>

                        <div class="label-content">
                            <div class="detail-line">${pid}</div>
                            <div class="detail-line">${productName}</div>
                            
                            <div class="detail-line">${details.split(' | ').join('<br>')}</div>
                        </div>
                    </div>
                </div>
                `;
        }).join("")}
        </div>
        <script>
            // Automatically trigger the print dialog after content loads
            window.onload = () => {
                setTimeout(() => {
                    window.print();
                    // window.close();
                }, 300);
            };
        </script>
    </body>
    </html>`;

        // 4. Write content and close the document
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const directPrintQRWithImage1 = async (qrData = [], pageSize) => {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return alert("Popup blocked. Please allow popups.");

        const base64Images = await Promise.all(
            qrData.map(qr => QRCode.toDataURL(qr.text))
        );

        const isRoller = pageSize === "Roller"; // 4x3 label
        const html = `
        <html>
            <head>
                <title>QR Print</title>
                <style>
                    body { font-family: sans-serif; margin: 0; padding: 20px; }
                    .grid {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 12px;
                        flex-direction: ${isRoller ? "column" : "row"};
                    }
                    .qr-box {
                        width: ${isRoller ? "100%" : "23%"};
                        text-align: center;
                        page-break-inside: avoid;
                        margin-bottom: ${isRoller ? "10px" : "0"};
                    }
                    .qr-box img {
                        width: ${isRoller ? "200px" : "128px"};
                        height: ${isRoller ? "200px" : "128px"};
                    }
                    .qr-box .label {
                        font-size: 12px;
                        margin-top: 5px;
                        word-break: break-word;
                    }
                </style>
            </head>
            <body>
                <div class="grid">
                    ${qrData.map((qr, i) => `
                        <div class="qr-box">
                            <img src="${base64Images[i]}" />
                            <div class="label">${qr.label}</div>
                        </div>
                    `).join("")}
                </div>
                <script>
                    window.onload = () => {
                        setTimeout(() => {
                            window.print();
                            window.close();
                        }, 300);
                    };
                </script>
            </body>
        </html>`;

        printWindow.document.write(html);
        printWindow.document.close();
    };


    const handlePrintQR = () => {
        const qrData = [];

        if (modalData.tracking_inventory_type === "Track Batches" && qrFor.includes("Serial/Batch Details")) {
            stockInformation
                .filter(item => item.checked)
                .forEach(item => {
                    const quantity = parseInt(item.Quantity, 10) || 0;
                    const extraFields = [];
                    if (item.Expiry_Date) extraFields.push(`Expiry_Date:${item.Expiry_Date}`);
                    if (item.Manufacture_Batch) extraFields.push(`Manufacture_Batch:${item.Manufacture_Batch}`);
                    if (item.Manufacture_Date) extraFields.push(`Manufacture_Date:${item.Manufacture_Date}`);
                    const extraText = extraFields.length > 0 ? " | " + extraFields.join(" | ") : "";
                    for (let i = 0; i < quantity; i++) {
                        qrData.push({
                            uid: `${modalData.pid}-${item.Batch_Reference}-${i + 1}`,
                            text: `Pid:${modalData.pid} | Pname:${modalData.product_name} | Ptype:${modalData.tracking_inventory_type} | Batch:${item.Batch_Reference} | Batch_uid:${item.uid}${extraText}`,
                            label: `${modalData.product_name} (Batch: ${item.Batch_Reference}) ${extraText}`
                        });
                    }
                });
        } else if (modalData.tracking_inventory_type === "Track Serial Number" && qrFor.includes("Serial/Batch Details")) {
            stockInformation
                .filter(item => item.checked)
                .forEach(item => {
                    qrData.push({
                        uid: `${modalData.pid}-${item.serialName}`,
                        text: `Pid:${modalData.pid} | Pname:${modalData.product_name} | Ptype:${modalData.tracking_inventory_type}  | Serial:${item.serialName} | Serial_uid:${item.uid}`,
                        label: `${modalData.product_name} (SN: ${item.serialName})`
                    });
                });
        } else if (modalData.tracking_inventory_type === "None" || !qrFor.includes("Serial/Batch Details")) {
            const quantity = parseInt(quantityAdjust, 10) || 0;
            for (let i = 0; i < quantity; i++) {
                qrData.push({
                    uid: `${modalData.pid}-QTY-${i + 1}`,
                    text: `Pid:${modalData.pid} | Pname:${modalData.product_name} | Ptype:${modalData.tracking_inventory_type}`,
                    label: `${modalData.product_name}`
                });
            }
        }

        if (qrData.length === 0) {
            alert("No QR codes to generate. Please select items.");
            return;
        }
        if (pageSize == "1*4 (inch)") {
            directPrintQRWithImage(qrData, pageSize);
        }
        else if (pageSize == "1*4 (inch)") {
            directPrintQRWithImage2(qrData, pageSize);
        }
        else {
            directPrintQRWithImage1(qrData, pageSize);
        }

    };

    return (
        <Modal show={displayModal} onHide={() => { handleProductQrModalClose() }} size="md" centered>
            <Modal.Header>
                <Modal.Title>{modalData.product_name}</Modal.Title>
                <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { handleProductQrModalClose() }}>
                    <span className="svg-icon svg-icon-1">
                        <svg xmlns="http://www.w3.org/2000/svg" xlink="http://www.w3.org/1999/xlink" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
                            <g transform="translate(12.000000, 12.000000) rotate(-45.000000) translate(-12.000000, -12.000000) translate(4.000000, 4.000000)" fill="#000000">
                                <rect fill="#000000" x="0" y="7" width="16" height="2" rx="1" />
                                <rect fill="#000000" opacity="0.5" transform="translate(8.000000, 8.000000) rotate(-270.000000) translate(-8.000000, -8.000000)" x="0" y="7" width="16" height="2" rx="1" />
                            </g>
                        </svg>
                    </span>
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ?
                    <PageLoader />
                    :
                    <div
                        className="container"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "5px",
                            width: "100%",
                        }}
                    >
                        {/* Track Batches */}
                        {(modalData.tracking_inventory_type === "Track Batches" && stockInformation?.length > 0 && qrFor.includes("Serial/Batch Details")) && (
                            <div className="w-100">
                                <h5 className="mb-3">Batch-wise Quantity Adjustment</h5>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-success me-2"
                                        onClick={() => {
                                            const updated = stockInformation.map((item) => ({ ...item, checked: true }));
                                            setStockInformation(updated);
                                        }}
                                    >
                                        Check All
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={() => {
                                            const updated = stockInformation.map((item) => ({ ...item, checked: false }));
                                            setStockInformation(updated);
                                        }}
                                    >
                                        Uncheck All
                                    </button>
                                </div>
                                {stockInformation.map((batch, index) => (
                                    <div
                                        key={index}
                                        className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2"
                                    >
                                        <div className="form-check me-3">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={batch.checked || false}
                                                onChange={(e) => {
                                                    const updated = [...stockInformation];
                                                    updated[index].checked = e.target.checked;
                                                    setStockInformation(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="flex-grow-1">
                                            <strong>Batch:</strong> {batch.Batch_Reference}
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <label className="me-2 mb-0">Qty:</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={batch.Quantity}
                                                className="form-control form-control-sm w-100px"
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value, 10);
                                                    const maxQty = batch.OriginalQuantity;
                                                    if (!isNaN(value) && value > 0 && value <= maxQty) {
                                                        const updated = [...stockInformation];
                                                        updated[index].Quantity = value;
                                                        setStockInformation(updated);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Track Serial Numbers */}
                        {modalData.tracking_inventory_type === "Track Serial Number" && stockInformation?.length > 0 && qrFor.includes("Serial/Batch Details") && (
                            <div className="w-100">
                                <h5 className="mb-3">Serial Numbers</h5>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-success me-2"
                                        onClick={() => {
                                            const updated = stockInformation.map((item) => ({ ...item, checked: true }));
                                            setStockInformation(updated);
                                        }}
                                    >
                                        Check All
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={() => {
                                            const updated = stockInformation.map((item) => ({ ...item, checked: false }));
                                            setStockInformation(updated);
                                        }}
                                    >
                                        Uncheck All
                                    </button>
                                </div>
                                <ul className="list-group">
                                    {stockInformation.map((serial, index) => (
                                        <li key={index} className="list-group-item d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input me-2"
                                                checked={serial.checked || false}
                                                onChange={(e) => {
                                                    const updated = [...stockInformation];
                                                    updated[index].checked = e.target.checked;
                                                    setStockInformation(updated);
                                                }}
                                            />
                                            {serial.serialName}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Simple Quantity */}
                        {(modalData.tracking_inventory_type === "None" || !qrFor.includes("Serial/Batch Details")) && (
                            <div className="w-100 text-center">
                                <h5 className="mb-3">Adjust Quantity</h5>
                                <input
                                    type="number"
                                    min="0"
                                    value={quantityAdjust || ""}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value) && value > 0 && value <= modalData.available_stock) {
                                            setQuantityAdjust(e.target.value)
                                        }
                                    }}
                                    className="form-control form-control-lg w-50 mx-auto"
                                />
                            </div>
                        )}
                    </div>
                }
            </Modal.Body>
            <Modal.Footer>
                {/* <button
                    className="btn btn-secondary"
                    onClick={handleProductQrModalClose()}
                >
                    Close
                </button> */}
                {write &&
                    <button
                        className="btn btn-success"
                        onClick={handlePrintQR}
                        disabled={
                            (qrFor.includes("Serial/Batch Details") && modalData.tracking_inventory_type !== "None" &&
                                !stockInformation.some(obj => obj.checked))
                        }
                    >
                        <i className="bi bi-qr-code me-2"></i> Print QR
                    </button>}
            </Modal.Footer>
        </Modal>
    );
};
